"use client";

import Image from "next/image";

import { IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";

interface NewsletterFooterBandProps {
  /** Parallax target, driven by `--circlePY` from `useScrollEffects`. */
  circleRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

/**
 * The ramp: the oxide wash that keeps the newsletter readable over the photo, then the footer's
 * solid Bulhões Green from 80% on (the board's ruling of 2026-09-29; the reference closed in
 * oxide). The last two wash stops turn to green so the photo resolves into the footer without a
 * seam.
 */
const GRADIENT =
  "linear-gradient(180deg," +
  "rgba(61,63,57,.74) 0%," +
  "rgba(61,63,57,.60) 18%," +
  "rgba(61,63,57,.52) 38%," +
  "rgba(61,63,57,.62) 55%," +
  "color-mix(in srgb, var(--tb-green) 80%, transparent) 66%," +
  "color-mix(in srgb, var(--tb-green) 92%, transparent) 74%," +
  "var(--tb-green) 80%," +
  "var(--tb-green) 100%)";

/**
 * The dark band that closes the page: newsletter + footer over one background.
 *
 * Neither section owns a background, which is what makes the seam between them
 * disappear. This wrapper stacks the green fill, the parallax photo and the
 * gradient that resolves one into the other.
 */
export function NewsletterFooterBand({ circleRef, children }: NewsletterFooterBandProps) {
  return (
    <div className="tb-dark-band relative overflow-hidden bg-[var(--tb-green)] text-[var(--tb-porcelain)]">
      <div
        ref={circleRef}
        className="absolute inset-x-0 top-0 h-[calc(var(--tb-circle-h)+240px)] will-change-transform motion-reduce:!transform-none"
        style={{ transform: "translate3d(0, var(--circlePY, 0px), 0) scale(1.10)" }}
      >
        {/*
          The "Para convidados" photo the board asked for on 2026-10-05, in a file of its own.
          It used to read `presentear.jpg`, the banner's and the card's file — so when the new
          Hero 1 replaced that photo on 2026-10-06, this band changed with it, which nobody had
          asked for. Its own file is what keeps the closing band where the board put it while the
          banner follows whatever the brand team ships next.
        */}
        <Image
          src="/images/homepage/newsletter-presentear.jpg"
          alt="Caixas de presente Tania Bulhões"
          fill
          sizes={IMAGE_SIZES_PRESETS.fullBleed}
          // Full-bleed banner behind the whole closing band — banner-class, so
          // `high` (see IMAGE_QUALITY).
          quality={IMAGE_QUALITY.high}
          className="object-cover object-center"
        />
      </div>

      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[calc(var(--tb-circle-h)+400px)]"
        style={{ background: GRADIENT }}
      />

      {children}
    </div>
  );
}
